import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";

import styled from "styled-components";

import {
  updateObstacle,
  updatePolyLine,
} from "../components/PotreeUtil/UpdatePolyLine";
import {
  useAreaGenerateStore,
  useCustomAlertStore,
  useDownloadModalStore,
  useEventStore,
  usePathStore,
  usePointStore,
} from "../Store/store";
import { createEventHandlers } from "../components/PotreeUtil/PotreeEvent";

import { getTileset } from "../api/ProjectAPI";
import { useNavigate, useParams } from "react-router-dom";
import {
  handlePathReset,
  handleEquipmentPathReset,
} from "../components/PotreeUtil/PathRemove_Create";
import { generateUniformWaypoints } from "../components/PotreeUtil/generateUniformWaypoints";

const LoadingBox = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 18px;
`;

export default function PotreeViewer2() {
  // Potree 뷰어 인스턴스 관리
  const containerRef = useRef(null);
  // Potree 뷰어 로딩 상태 관리
  const [isLoaded, setIsLoaded] = useState(true);
  const { potreeUrl, setPotreeUrl } = usePointStore();
  // 파일 아이디 관리
  const { projectId, fileId } = useParams();

  // 경로 수정 모드 관리
  const {
    viewer,
    setViewer,
    pathEditMode,
    pathGenerateMode,
    obstacleMode,
    setObstacleModal,
    setEachObstaclePoints,
    closedObstacleArea,
    setClosedObstacleArea,
    setUpdatePolyLine,
    nodes,
    markers,
    edges,
    ribbonMeshs,
    polyline,
    ribbonMesh,
    vehicleSpec,
    waypoints,
    setWaypoints,
    setPathGenerateMode,
    setPathEditMode,
    setAllObstaclePoints,
    setMarkers,
    setNodes,
    setEdges,
    setRibbonMeshs,
    setPolyline,
    setRibbonMesh,
    pathData,
    selectedEquipmentId,
    equipmentPaths,
    resetEquipmentPath,
    hideAllEquipmentMarkers,
    showEquipmentMarkers,
    updateEquipmentPath,
    showAllEquipmentMarkers,
    setEquipmentWaypoints,
    equipmentWaypoints,
  } = usePathStore();

  const { setObstacleInfo, obstacleInfo } = usePointStore();

  const { selectedAreaColor } = useAreaGenerateStore();
  const { setCancelObstacle } = useEventStore();
  const [selectedMarker, setSelectedMarker] = useState(null); // 선택된 마커 인덱스
  const [isDragging, setIsDragging] = useState(false);

  const [obstaclePoints, setObstaclePoints] = useState([]);
  const [obstacleMarkers, setObstacleMarkers] = useState([]);
  const [obstacleInfoArray, setObstacleInfoArray] = useState([]); // 장애물 영역 데이터
  const currentDraftingIdRef = useRef(null);
  const navigate = useNavigate();
  const { showInfoAlert } = useCustomAlertStore();

  const waypointsRef = useRef([]);

  // waypoints 저장
  useEffect(() => {
    waypointsRef.current = waypoints;
    window.currentWaypoints = waypoints; // 전역에 직접 할당
  }, [waypoints]);

  // updatePolyLine 함수를 Store에 저장
  useEffect(() => {
    setUpdatePolyLine(updatePolyLine);
  }, [setUpdatePolyLine]);

  // 좌표 추출 함수
  useEffect(() => {
    // 경로 생성 모드일 때만 좌표 추출
    const currentEquipmentPath = equipmentPaths[selectedEquipmentId];

    if (currentEquipmentPath && currentEquipmentPath.edges.length > 0) {
      const lastPolyline =
        currentEquipmentPath.edges[currentEquipmentPath.edges.length - 1]; // 마지막 센터라인 가져오기
      const geometry = lastPolyline.geometry; // 센터라인 기하 가져오기
      const positions = geometry.attributes.position.array; // 센터라인 좌표 가져오기
      const coordinates = []; // 좌표 배열

      for (let i = 0; i < positions.length; i += 3) {
        // 좌표 배열 생성
        coordinates.push({
          x: positions[i],
          y: positions[i + 1],
          z: positions[i + 2],
        });
      }

      // 균일한 간격의 중간점 생성
      const waypoints = generateUniformWaypoints(coordinates, 1.0);

      setWaypoints(waypoints);
      setEquipmentWaypoints(selectedEquipmentId, waypoints);

      const allWaypoints = {
        ...JSON.parse(localStorage.getItem("allWaypoints") || "{}"),
        [selectedEquipmentId]: waypoints,
      };
      localStorage.setItem("allWaypoints", JSON.stringify(allWaypoints));
    }
  }, [
    pathGenerateMode,
    equipmentPaths,
    selectedEquipmentId,
    pathEditMode,
    pathData,
  ]);

  // 수정 모드일 때만 마커 색상 변경
  useEffect(() => {
    hideAllEquipmentMarkers();
    showEquipmentMarkers(selectedEquipmentId);
  }, [selectedEquipmentId]);

  // Potree 뷰어 로딩 함수 실행
  useEffect(() => {
    window.THREE = THREE;

    // Potree 뷰어 로딩 함수
    const loadPotree = async () => {
      if (!window.Potree || !containerRef.current) return;

      try {
        const potreeViewer = new window.Potree.Viewer(containerRef.current); // 뷰어 지정

        potreeViewer.setEDLEnabled(true); // 포인트 클라우드에 깊이감과 입체감을 추가
        potreeViewer.setFOV(60); // 카메라의 시야각
        potreeViewer.setPointBudget(1000000); //한 번에 렌더링  viewer.scene.renderer.setClearColor(0x87ceeb);할 수 있는 최대 포인트 수
        potreeViewer.loadGUI(() => {
          potreeViewer.setLanguage("ko");
        });

        if (window.proj4) {
          // proj4 정의가 없는 경우 추가
          if (!window.proj4.defs["EPSG:5186"]) {
            window.proj4.defs(
              "EPSG:5186",
              "+proj=tmerc +lat_0=38 +lon_0=127.5 +k=1 +x_0=200000 +y_0=500000 +ellps=GRS80 +units=m +no_defs",
            );
          }
        }

        setViewer(potreeViewer);
        setIsLoaded(false);
      } catch (err) {
        console.warn("좌표계 설정 오류:", err);
      }
    };
    loadPotree();

    // 컴포넌트 언마운트 시 뷰어 인스턴스 정리
    return () => {
      if (viewer) {
        // 2. 렌더 루프 정지
        viewer.renderArea.removeEventListener("resize", viewer.onResize, false);

        // 3. Three.js 리소스 해제
        viewer.scene.scene.traverse((obj) => {
          if (!obj) return;

          // Geometry 해제
          if (obj.geometry) {
            obj.geometry.dispose();
          }

          // Material 해제
          if (obj.material) {
            if (Array.isArray(obj.material)) {
              obj.material.forEach((m) => m.dispose && m.dispose());
            } else {
              obj.material.dispose && obj.material.dispose();
            }
          }

          // Texture 해제
          if (obj.material && obj.material.map) {
            obj.material.map.dispose();
          }
        });
        // 4. DOM에서 제거
        if (viewer.renderArea && viewer.renderArea.parentNode) {
          viewer.renderArea.parentNode.removeChild(viewer.renderArea);
        }
      }
    };
  }, []);

  // 경로 초기화
  useEffect(() => {
    handlePathReset(
      viewer,
      setPathGenerateMode,
      setPathEditMode,
      resetEquipmentPath,
      selectedEquipmentId,
    );
  }, []);

  // 포인트 클라우드 로드
  useEffect(() => {
    if (!viewer || isLoaded) return;

    // 포인트 클라우드 로드 함수
    const loadPointCloud = async () => {
      if (!viewer || !window.Potree) return;

      const convertToAbsoluteUrl = (url, baseIP) => {
        if (baseIP === undefined) return url;
        if (url.startsWith("http://") || url.startsWith("https://")) return url;
        const baseUrl = baseIP || window.location.origin;
        return `${baseUrl}${url.startsWith("/") ? url : "/" + url}`;
      };

      try {
        const response = await getTileset(fileId, "potree");
        let url = response.baseUrl;
        const fileServerIP = process.env.REACT_APP_BASE_URL;
        url = convertToAbsoluteUrl(url, fileServerIP);
        const result = await window.Potree.loadPointCloud(url);
        const pointCloud = result.pointcloud;

        viewer.scene.addPointCloud(pointCloud);
        viewer.fitToScreen();

        // 포인트 클라우드 조명 활성화
        const pointcloud = viewer.scene.pointclouds[0];
        if (pointcloud && pointcloud.material) {
          // 조명 활성화
          pointcloud.material.lights = true;

          // EDL 비활성화 (선택사항)
          pointcloud.material._useEDL = false;
        }
      } catch (err) {
        if (err.response.status === 500) {
          showInfoAlert("타일셋 조회 실패", "파일이 아직 생성 중입니다.");
          navigate(-1);
        }
      }
    };
    loadPointCloud();
  }, [isLoaded, viewer, potreeUrl]);

  // 포인트 클라우드 클릭 이벤트 처리 (좌표 추출, 마커 생성)
  useEffect(() => {
    if (!viewer || !viewer.scene) {
      return;
    }

    // 별도의 파일로 분리한 이벤트 핸들러 생성
    const handlers = createEventHandlers({
      pathGenerateMode,
      obstacleMode,
      containerRef,
      viewer,
      markers,
      nodes,
      polyline,
      ribbonMesh,
      vehicleSpec,
      edges,
      ribbonMeshs,
      obstaclePoints,
      obstacleMarkers,
      setObstacleModal,
      setObstaclePoints,
      updateObstacle,
      setObstacleMarkers,
      setEachObstaclePoints,
      pathEditMode,
      setSelectedMarker,
      setIsDragging,
      selectedMarker,
      isDragging,
      obstacleInfoArray,
      setObstacleInfoArray,
      closedObstacleArea,
      setClosedObstacleArea,
      updatePolyLine,
      currentDraftingIdRef,
      selectedAreaColor,
      obstacleInfo,
      setObstacleInfo,
      selectedEquipmentId,
      equipmentPaths,
      updateEquipmentPath,
      showAllEquipmentMarkers,
      setPathGenerateMode,
      setPathEditMode,
      resetEquipmentPath,
      handleEquipmentPathReset,
    });

    setCancelObstacle(handlers.cancleCurrentObstacle);

    // 이벤트 핸들러 등록
    containerRef.current?.addEventListener("click", handlers.handleClick);
    containerRef.current?.addEventListener(
      "mousedown",
      handlers.handleMouseDown,
    );
    containerRef.current?.addEventListener(
      "mousemove",
      handlers.handleMouseMove,
    );
    containerRef.current?.addEventListener("mouseup", handlers.handleMouseUp);
    containerRef.current?.addEventListener("keydown", handlers.handleKeyDown);

    // 클린업 함수로 리스너 제거
    return () => {
      containerRef.current?.removeEventListener("click", handlers.handleClick);
      containerRef.current?.removeEventListener(
        "mousedown",
        handlers.handleMouseDown,
      );
      containerRef.current?.removeEventListener(
        "mousemove",
        handlers.handleMouseMove,
      );
      containerRef.current?.removeEventListener(
        "mouseup",
        handlers.handleMouseUp,
      );
      containerRef.current?.removeEventListener(
        "keydown",
        handlers.handleKeyDown,
      );
    };
  }, [
    viewer,
    pathGenerateMode,
    pathEditMode,
    selectedMarker,
    isDragging,
    obstacleMode,
  ]);

  return (
    <div className="potree-container">
      <div ref={containerRef} className="potree-render-area" />

      {/* 로딩 중 표시 */}
      {isLoaded && <LoadingBox>Potree 로딩 중...</LoadingBox>}
    </div>
  );
}
